/**
 * DealOwnerEditor — inline multi-user owner picker for a single SSP deal_id.
 * Commits on every toggle; owners control who sees this deal in reporting.
 */
import { useState, useMemo, useRef, useEffect } from "react";
import { UserPlus, X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { setDealOwners, shortUserLabel, fullUserLabel } from "./dealOwnersApi";

const DealOwnerEditor = ({ token, dealId, users = [], value = [], onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const menuRef = useRef(null);

  const usersById = useMemo(() => {
    const m = new Map();
    users.forEach((u) => m.set(u.id, u));
    return m;
  }, [users]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => `${u.name || ""} ${u.email} ${u.company || ""}`.toLowerCase().includes(q));
  }, [users, query]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) { setOpen(false); setQuery(""); }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const commit = async (nextIds) => {
    setSaving(true);
    try {
      await setDealOwners(token, dealId, nextIds);
      onChange && onChange(nextIds);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save owners");
    } finally {
      setSaving(false);
    }
  };

  const toggleUser = (userId) =>
    commit(value.includes(userId) ? value.filter((u) => u !== userId) : [...value, userId]);

  return (
    <div className="relative inline-flex items-center gap-1 flex-wrap" data-testid={`deal-owner-editor-${dealId}`}>
      {value.map((uid) => (
        <span
          key={uid}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono bg-[#22c55e]/10 text-[#22c55e] border-[#22c55e]/30"
          title={fullUserLabel(usersById.get(uid))}
          data-testid={`owner-chip-${dealId}-${uid}`}
        >
          {shortUserLabel(usersById.get(uid))}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); commit(value.filter((u) => u !== uid)); }}
            className="opacity-60 hover:opacity-100"
            disabled={saving}
            aria-label="Remove owner"
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-dashed border-white/15 text-[10px] font-mono text-[#ebebeb]/50 hover:text-[#ebebeb]/80 hover:border-white/30"
        data-testid={`add-owner-btn-${dealId}`}
      >
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
        {value.length === 0 ? "Assign" : ""}
      </button>

      {open && (
        <div
          ref={menuRef}
          className="absolute top-full left-0 mt-1 z-50 w-72 bg-[#1e2124] border border-[#2a2d31] rounded-lg shadow-xl py-2"
          data-testid={`owner-menu-${dealId}`}
        >
          <div className="px-2 pb-2 border-b border-[#2a2d31]">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search user by name or email…"
              className="w-full bg-[#1a1c1f] border border-[#2a2d31] rounded px-2 py-1 text-xs font-mono text-[#ebebeb] focus:outline-none focus:border-[#ff3737]"
              data-testid={`owner-search-${dealId}`}
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-[11px] text-[#ebebeb]/40 font-mono">No users match</div>
            )}
            {filtered.slice(0, 100).map((u) => {
              const on = value.includes(u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggleUser(u.id)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs font-mono text-[#ebebeb]/80 hover:bg-white/5 text-left"
                  data-testid={`owner-option-${u.id}`}
                >
                  <span className="truncate">
                    <span className="text-[#ebebeb]/90">{u.name || u.email}</span>
                    <span className="text-[#ebebeb]/40 text-[10px] block">{u.email} · {u.role}</span>
                  </span>
                  {on && <Check className="w-3.5 h-3.5 text-[#22c55e] flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DealOwnerEditor;
